import { useState } from 'react';
import {
  Button,
  Field,
  FieldDescription,
  FieldError,
  FieldLabel,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@monksflow/monks-ui';
import { XIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { TRANSPARENCY_VALUES, type SpecValueKind } from '@inference.engine/ad-formats/web';
import { SIDES, specEditor, type Side, type ValueDraft } from '../lib/spec-draft';
import { transparencyLabel } from './provenance';

/* eslint-disable i18next/no-literal-string -- examples of the value syntax, which is not translated */
const EXAMPLES: Partial<Record<SpecValueKind, string>> = {
  ratio: '9:16',
  ratioList: '1.91:1..4:5, 9:16',
  text: 'free text',
  number: '60',
  numberSet: '23.976..60 or 24, 25, 30',
  boolean: 'true or false',
};
const COMMA_EXAMPLES: Partial<Record<string, string>> = {
  containers: 'mp4, mov',
};
const DEFAULT_COMMA_EXAMPLE = 'jpg, png';
/* eslint-enable i18next/no-literal-string */

interface SpecValueFieldsProps {
  kind: SpecValueKind;
  property: string;
  draft: ValueDraft;
  error: string | undefined;
  onChange: (next: ValueDraft) => void;
}

/** The inputs for one property's value, shaped to what the property holds. */
export function SpecValueFields({ kind, property, draft, error, onChange }: SpecValueFieldsProps) {
  const { t } = useTranslation('admin');
  const set = (patch: Partial<ValueDraft>) => onChange({ ...draft, ...patch });
  const errorLine = error ? <FieldError>{error}</FieldError> : null;

  switch (specEditor(kind, property)) {
    case 'size':
      return (
        <div className="flex flex-col gap-xs">
          <div className="grid grid-cols-2 gap-md">
            <PixelField
              id="spec-width"
              label={t('admin.adFormats.fields.width')}
              value={draft.width}
              onChange={(width) => set({ width })}
            />
            <PixelField
              id="spec-height"
              label={t('admin.adFormats.fields.height')}
              value={draft.height}
              onChange={(height) => set({ height })}
            />
          </div>
          <FieldDescription>{t('admin.adFormats.spec.sizeHint')}</FieldDescription>
          {errorLine}
        </div>
      );
    case 'margins':
      return (
        <div className="flex flex-col gap-xs">
          <div className="grid grid-cols-2 gap-md sm:grid-cols-4">
            {SIDES.map((side) => (
              <PixelField
                key={side}
                id={`spec-margin-${side}`}
                label={marginLabel(t, side)}
                value={draft.margins[side]}
                onChange={(next) => set({ margins: { ...draft.margins, [side]: next } })}
              />
            ))}
          </div>
          <FieldDescription>{t('admin.adFormats.spec.marginsHint')}</FieldDescription>
          {errorLine}
        </div>
      );
    case 'transparency':
      return (
        <Field>
          <FieldLabel htmlFor="spec-transparency">{t('admin.adFormats.spec.value')}</FieldLabel>
          <Select value={draft.text} onValueChange={(text) => set({ text })}>
            <SelectTrigger id="spec-transparency">
              <SelectValue placeholder={t('admin.adFormats.spec.chooseTransparency')} />
            </SelectTrigger>
            <SelectContent>
              {TRANSPARENCY_VALUES.map((value) => (
                <SelectItem key={value} value={value}>
                  {transparencyLabel(t, value)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errorLine}
        </Field>
      );
    case 'fileSize':
      return (
        <Field>
          <FieldLabel htmlFor="spec-kb">{t('admin.adFormats.spec.fileSizeKb')}</FieldLabel>
          <Input
            id="spec-kb"
            inputMode="decimal"
            value={draft.text}
            onChange={(event) => set({ text: event.target.value })}
          />
          {errorLine}
        </Field>
      );
    case 'commaList':
      return (
        <Field>
          <FieldLabel htmlFor="spec-value">{t('admin.adFormats.spec.value')}</FieldLabel>
          <Input
            id="spec-value"
            value={draft.text}
            onChange={(event) => set({ text: event.target.value })}
          />
          <FieldDescription>
            {t('admin.adFormats.spec.commaListHint', {
              example: COMMA_EXAMPLES[property] ?? DEFAULT_COMMA_EXAMPLE,
            })}
          </FieldDescription>
          {errorLine}
        </Field>
      );
    case 'list':
      return <ListEditor items={draft.items} error={error} onChange={(items) => set({ items })} />;
    case 'text':
      return (
        <Field>
          <FieldLabel htmlFor="spec-value">{t('admin.adFormats.spec.value')}</FieldLabel>
          <Input
            id="spec-value"
            value={draft.text}
            onChange={(event) => set({ text: event.target.value })}
          />
          <FieldDescription>
            {t('admin.adFormats.spec.valueHint', { example: EXAMPLES[kind] ?? '' })}
          </FieldDescription>
          {errorLine}
        </Field>
      );
  }
}

function PixelField({
  id,
  label,
  value,
  onChange,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (next: string) => void;
}) {
  return (
    <Field>
      <FieldLabel htmlFor={id}>{label}</FieldLabel>
      <Input
        id={id}
        inputMode="numeric"
        value={value}
        onChange={(event) => onChange(event.target.value)}
      />
    </Field>
  );
}

function ListEditor({
  items,
  error,
  onChange,
}: {
  items: string[];
  error: string | undefined;
  onChange: (next: string[]) => void;
}) {
  const { t } = useTranslation('admin');
  const [entry, setEntry] = useState('');

  const add = () => {
    const next = entry.trim();
    if (next && !items.includes(next)) onChange([...items, next]);
    setEntry('');
  };

  return (
    <TooltipProvider>
      <Field>
        <FieldLabel htmlFor="spec-entry">{t('admin.adFormats.spec.newEntry')}</FieldLabel>
        <div className="flex gap-xs">
          <Input
            id="spec-entry"
            value={entry}
            onChange={(event) => setEntry(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                add();
              }
            }}
          />
          <Button variant="outline" onClick={add} disabled={!entry.trim()}>
            {t('admin.adFormats.spec.addEntry')}
          </Button>
        </div>
        {items.length > 0 && (
          <ul className="flex flex-wrap gap-xs">
            {items.map((item) => (
              <li
                key={item}
                className="flex items-center gap-2xs rounded-md border border-border py-2xs pr-2xs pl-xs text-body-sm"
              >
                {item}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="size-6"
                      aria-label={t('admin.adFormats.spec.removeEntry', { entry: item })}
                      onClick={() => onChange(items.filter((other) => other !== item))}
                    >
                      <XIcon aria-hidden />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    {t('admin.adFormats.spec.removeEntry', { entry: item })}
                  </TooltipContent>
                </Tooltip>
              </li>
            ))}
          </ul>
        )}
        {error && <FieldError>{error}</FieldError>}
      </Field>
    </TooltipProvider>
  );
}

type AdminT = ReturnType<typeof useTranslation<'admin'>>['t'];

function marginLabel(t: AdminT, side: Side): string {
  switch (side) {
    case 'top':
      return t('admin.adFormats.spec.margins.top');
    case 'right':
      return t('admin.adFormats.spec.margins.right');
    case 'bottom':
      return t('admin.adFormats.spec.margins.bottom');
    case 'left':
      return t('admin.adFormats.spec.margins.left');
  }
}
